var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useRef } from 'react';
import { useMutation, useQueryClient, } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
/**
 * Get a callback to call the dataProvider.addChildNode() method, the result and the loading state.
 *
 * @param {string} resource
 * @param {Params} params The addChildNode parameters { data }
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 *
 * @typedef Params
 * @prop params.data The record to add as a child node, e.g. { title: 'hello, world' }
 * @prop params.parentId The identifier of the parent node, e.g. 123
 *
 * @returns The current mutation state. Destructure as [addChildNode, { data, error, isLoading }].
 *
 * The return value updates according to the request state:
 *
 * - initial: [addChildNode, { isLoading: false, isIdle: true }]
 * - start:   [addChildNode, { isLoading: true }]
 * - success: [addChildNode, { data: [data from response], isLoading: false, isSuccess: true }]
 * - error:   [addChildNode, { error: [error from response], isLoading: false, isError: true }]
 *
 * The addChildNode() function must be called with a resource and a parameter object: addChildNode(resource, { data, meta }, options)
 *
 * This hook uses react-query useMutation under the hood.
 * This means the state object contains mutate, isIdle, reset and other react-query methods.
 *
 * @see https://react-query.tanstack.com/reference/useMutation
 *
 * @example // set params when calling the addChildNode callback
 *
 * import { useAddChildNode } from '@react-admin/ra-tree';
 *
 * const AddNodeButton = ({ record }) => {
 *     const node = { title: 'a child node', parentId: record.id };
 *     const [addChildNode, { isLoading, error }] = useAddChildNode();
 *     const handleClick = () => {
 *         addChildNode('nodes', { data: node })
 *     }
 *     if (error) { return <p>ERROR</p>; }
 *     return <button disabled={isLoading} onClick={() => addChildNode()}>Add a new child node</button>;
 * };
 *
 * @example // set params when calling the hook
 *
 * import { useAddChildNode } from '@react-admin/ra-tree';
 *
 * const AddNodeButton = ({ record }) => {
 *     const node = { title: 'a child node', parentId: record.id };
 *     const [addChildNode, { isLoading, error }] = useAddChildNode('nodes', { data: node });
 *     if (error) { return <p>ERROR</p>; }
 *     return <button disabled={isLoading} onClick={() => addChildNode()}>Add a new child node</button>;
 * };
 *
 * @example // TypeScript
 * const [addChildNode, { data }] = useAddChildNode<Product>('products', { data: product });
 *                    \-- data is Product
 */
export var useAddChildNode = function (resource, params, options) {
    if (params === void 0) { params = {}; }
    if (options === void 0) { options = {}; }
    var dataProvider = useDataProvider();
    var queryClient = useQueryClient();
    var paramsRef = useRef(params);
    var mutation = useMutation(__assign(__assign({ mutationFn: function (_a) {
            var _b = _a === void 0 ? {} : _a, _c = _b.resource, callTimeResource = _c === void 0 ? resource : _c, _d = _b.data, callTimeData = _d === void 0 ? paramsRef.current.data : _d, _e = _b.parentId, callTimeParentId = _e === void 0 ? paramsRef.current.parentId : _e, _f = _b.position, callPosition = _f === void 0 ? paramsRef.current.position : _f, _g = _b.meta, callTimeMeta = _g === void 0 ? paramsRef.current.meta : _g;
            if (!callTimeResource) {
                throw new Error('useAddChildNode mutation requires a non-empty resource');
            }
            if (callTimeData == null) {
                throw new Error('useAddChildNode mutation requires non-empty data');
            }
            if (callTimeParentId == null) {
                throw new Error('useAddChildNode mutation requires a non-empty parent id');
            }
            return dataProvider
                .addChildNode(callTimeResource, {
                data: callTimeData,
                parentId: callTimeParentId,
                position: callPosition,
                meta: callTimeMeta,
            })
                .then(function (_a) {
                var data = _a.data;
                return data;
            });
        } }, options), { onSuccess: function (data, variables, context) {
            if (variables === void 0) { variables = {}; }
            var _a = variables.resource, callTimeResource = _a === void 0 ? resource : _a;
            queryClient.setQueryData([callTimeResource, 'getOne', { id: String(data.id) }], data);
            if (options.onSuccess) {
                options.onSuccess(data, variables, context);
            }
            // call-time success callback is executed by react-query
        } }));
    var addRootNode = function () {
        var args_1 = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args_1[_i] = arguments[_i];
        }
        return __awaiter(void 0, __spreadArray([], args_1, true), void 0, function (callTimeResource, callTimeParams, callTimeOptions) {
            var returnPromise, otherOptions;
            if (callTimeResource === void 0) { callTimeResource = resource; }
            if (callTimeParams === void 0) { callTimeParams = {}; }
            if (callTimeOptions === void 0) { callTimeOptions = {}; }
            return __generator(this, function (_a) {
                returnPromise = callTimeOptions.returnPromise, otherOptions = __rest(callTimeOptions, ["returnPromise"]);
                if (returnPromise) {
                    return [2 /*return*/, mutation.mutateAsync(__assign({ resource: callTimeResource }, callTimeParams), otherOptions)];
                }
                mutation.mutate(__assign({ resource: callTimeResource }, callTimeParams), otherOptions);
                return [2 /*return*/];
            });
        });
    };
    return [addRootNode, mutation];
};
